import { useRef, useState } from 'react';
import { Button, FOCUS_RING_FLUSH, Input } from '../ui';
import { TASK_GRID } from './taskGrid';
import { ExpandIcon, PlusIcon } from '../icons';

/**
 * Add a task with just a title. Enter commits and keeps focus, so a list can be
 * typed straight down without reaching for the mouse.
 *
 * Two shapes. `row` sits as the last item of a status group and lays itself out
 * on the shared column template, so the input lines up under the titles above
 * it and adding here inherits that group's status. `block` is the standalone
 * box used for subtasks inside the panel.
 *
 * It does not create anything itself: it collects a title, hands it over and
 * clears. Waiting for the POST is what made typing a list stop dead for a round
 * trip between each line -- so the caller posts it in the background, shows the
 * row straight away and owns any failure, which has to outlive this box either
 * way. There is nothing to disable while saving, because as far as this
 * component is concerned nothing is.
 */
export function TaskComposer({
  variant = 'block',
  grid = TASK_GRID,
  placeholder = 'Add a task and press Enter',
  onSubmit,
  onExpand,
}: {
  variant?: 'row' | 'block';
  /** `row` only: the template the list around it is drawn on, so the add row
   *  keeps aligning with the rows above it. See taskGrid.ts. */
  grid?: string;
  placeholder?: string;
  /** The trimmed, non-empty title. Never called with blank. */
  onSubmit: (title: string) => void;
  /** Hand the half-typed title to the full panel instead of creating here. */
  onExpand?: (title: string) => void;
}) {
  const [title, setTitle] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  function submit() {
    const trimmed = title.trim();
    if (!trimmed) return;
    // Cleared first: the next title is already being typed by the time anything
    // comes back, and clearing on the response would eat those keystrokes.
    setTitle('');
    onSubmit(trimmed);
    // Enter never left the box; the Add button does, and the next line is
    // almost always the reason somebody is here.
    inputRef.current?.focus();
  }

  const commonInputProps = {
    value: title,
    placeholder,
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => setTitle(event.target.value),
    onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== 'Enter') return;
      // Would otherwise submit an enclosing form.
      event.preventDefault();
      submit();
    },
  };

  if (variant === 'row') {
    return (
      <li
        // The input only occupies the title column, so a click on the padding or
        // anywhere right of it would land on the row and focus nothing -- and
        // with a transparent field that reads as "typing does not work".
        onClick={() => inputRef.current?.focus()}
        // `py-1`, matching TaskRow. At `py-0.5` the add row was four pixels
        // shorter than every task above it, which broke the rhythm of the list
        // at the bottom of every single group.
        className={`${grid} group cursor-text px-2 py-1 transition focus-within:bg-honey-50/60`}
      >
        {/* Doubles as the affordance: without it the row is an unadorned line of
            placeholder text with nothing to say it can be typed into. */}
        <span className="text-stone-400 transition group-hover:text-stone-500 group-focus-within:text-honey-600">
          <PlusIcon />
        </span>

        <input
          {...commonInputProps}
          ref={inputRef}
          aria-label={placeholder}
          // No ring of its own -- the row lights up on `focus-within` instead, so
          // the feedback covers the whole target rather than a transparent box
          // inside it. Removing the outline without replacing it is what made
          // this look inert.
          className="w-full min-w-0 bg-transparent py-1 text-dense text-stone-900 placeholder:text-stone-400 focus:outline-none"
        />

        {onExpand ? (
          <button
            type="button"
            title="Open the full form"
            aria-label="Open the full form"
            // Hidden until the row is in play: one of these per status group is
            // four permanent icons for a control most adds never need. Focus,
            // not just hover, so it is reachable by keyboard.
            className={`flex h-5 w-5 items-center justify-center rounded text-stone-500 opacity-0 transition hover:bg-stone-200/70 hover:text-stone-900 focus:opacity-100 group-focus-within:opacity-100 group-hover:opacity-100 ${FOCUS_RING_FLUSH}`}
            onClick={() => {
              // Cleared and trimmed: the panel owns this text now, and leaving a
              // copy behind reads as a submit that silently failed.
              const handoff = title.trim();
              setTitle('');
              onExpand(handoff);
            }}
          >
            <ExpandIcon />
          </button>
        ) : (
          <span aria-hidden="true" />
        )}
      </li>
    );
  }

  return (
    <div className="flex gap-2">
      <Input compact {...commonInputProps} ref={inputRef} />
      {/* Secondary, not primary. Enter is the path this box is built around --
          it commits, clears and keeps focus so a list can be typed straight
          down -- and a filled accent button on a fallback control competes with
          the section's real primary action a few rows below it. */}
      <Button compact disabled={!title.trim()} onClick={submit}>
        <PlusIcon />
        Add
      </Button>
    </div>
  );
}
